import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";

// Uses same scaling config as ReelBack to stay perfectly aligned
const SPINE_CONFIG = {
  verticalOffset: -0.0195,
  horizontalOffset: 0,
  widthMultiplier: 1.45,
  heightMultiplier: 1.4,
  frameWidthRatio: 1,
  frameHeightRatio: 1,
};

/**
 * ReelBackDecorationController
 * Manages event animations (event_in, event_idle, event_out) from reel_background spine.
 * Renders behind ReelBack — separate spine instance, single responsibility.
 */
class ReelBackDecorationController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
  ) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.spine = null;
    this.isEventPlaying = false;
    this._dividerCountCache = Settings.REEL_COUNT - 1;
    this._cachedBounds = null; // Cache initial bounds to prevent animation-state drift

    requestAnimationFrame(() => {
      this.initializeSpine();
    });
  }

  initializeSpine() {
    this.spine = this.createSpine();
    if (!this.spine || !this.spine.skeleton || !this.spine.state) {
      return;
    }

    this.spine.position.set(0, 0);
    this.spine.zIndex = 1;
    this.spine.visible = true;
    this.spine.alpha = 0.001; // Hidden until event plays
    this.spine.autoUpdate = false;

    this.container.addChild(this.spine);
    this.applySpineScaling();
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "reel_background-skeleton";
      const atlasAlias = "reel_background-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  applySpineScaling() {
    if (!this.spine || !this.symbolHeight || !this.dimensions) {
      return;
    }

    // Use cached bounds to prevent animation-state drift on resize
    if (!this._cachedBounds) {
      const spineBounds = this.spine.getLocalBounds();
      if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
        requestAnimationFrame(() => {
          this.applySpineScaling();
        });
        return;
      }
      this._cachedBounds = {
        width: spineBounds.width,
        height: spineBounds.height,
      };
    }

    const dividerWidth = this.dividerDimensions?.width ?? 0;
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.widthMultiplier +
      dividerWidth * (this._dividerCountCache - 2);
    const targetFrameHeight =
      this.symbolHeight *
      Settings.SYMBOLS_VISIBLE *
      SPINE_CONFIG.heightMultiplier;

    const cfg = SPINE_CONFIG;
    const requiredSpineWidth = targetFrameWidth / (cfg.frameWidthRatio || 1);
    const requiredSpineHeight = targetFrameHeight / (cfg.frameHeightRatio || 1);

    const scaleX = requiredSpineWidth / this._cachedBounds.width;
    const scaleY = requiredSpineHeight / this._cachedBounds.height;
    this.spine.scale.set(scaleX, scaleY);
    this.spine.position.set(0, 0);
  }

  // Play event_in → event_idle sequence
  playEventIn() {
    if (!this.spine || !this.spine.state) return;

    this.isEventPlaying = true;
    this.spine.alpha = 1;
    this.spine.autoUpdate = true;

    this.spine.state.clearListeners();
    this.spine.state.clearTracks();

    // event_in (once) → event_idle (loop)
    this.spine.state.setAnimation(0, "event_in", false);
    this.spine.state.addAnimation(0, "event_idle", true, 0);
  }

  // Play event_out, then hide
  playEventOut() {
    if (!this.spine || !this.spine.state || !this.isEventPlaying) return;

    this.spine.state.clearListeners();
    this.spine.state.setAnimation(0, "event_out", false);

    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "event_out") {
          this.stopEvent();
        }
      },
    });
  }

  // Immediately stop and hide
  stopEvent() {
    if (!this.spine) return;

    this.isEventPlaying = false;
    this.spine.alpha = 0.001;
    this.spine.autoUpdate = false;
    if (this.spine.state) {
      this.spine.state.clearTracks();
      this.spine.state.clearListeners();
    }
  }

  // Responsive resize
  resizeDecoration(
    newSymbolWidth,
    newSymbolHeight,
    newDimensions,
    newDividerDimensions,
  ) {
    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;
    if (newDimensions) this.dimensions = newDimensions;
    if (newDividerDimensions) this.dividerDimensions = newDividerDimensions;

    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Cleanup
  cleanup() {
    if (this.spine && !this.spine.destroyed) {
      try {
        if (this.spine.state) {
          this.spine.state.clearTracks();
          this.spine.state.clearListeners();
        }
        if (this.spine.parent) {
          this.spine.parent.removeChild(this.spine);
        }
        this.spine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }
    this.spine = null;
    this.container = null;
    this.isEventPlaying = false;
  }
}

export default ReelBackDecorationController;
